import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  CorporateDesignStyle,
  HeaderVariation,
  HeroVariation,
  ServicesVariation,
  AboutVariation,
  CtaVariation,
  FooterVariation,
  SitePage,
  PageSection,
} from '../../types';
import {
  Paintbrush,
  Check,
  CheckCircle2,
  Sparkles,
  Layout,
  Type,
  Palette,
  Sliders,
  Eye,
  Save,
  Layers,
  Building,
  Zap,
  Crown,
  MapPin,
} from 'lucide-react';

interface DesignStylePreset {
  id: CorporateDesignStyle;
  name: string;
  badge: string;
  tagline: string;
  description: string;
  icon: any;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  headingFont: string;
  headerVariation: HeaderVariation;
  heroVariation: HeroVariation;
  servicesVariation: ServicesVariation;
  aboutVariation: AboutVariation;
  ctaVariation: CtaVariation;
  footerVariation: FooterVariation;
}

const DESIGN_STYLES: DesignStylePreset[] = [
  {
    id: 'STYLE_CORPORATE',
    name: 'STYLE 01 — Corporate',
    badge: 'CİDDİ & KURUMSAL',
    tagline: 'Temiz, ciddi, kurumsal ve güven odaklı tasarım mimarisi.',
    description: 'B2B şirketler, danışmanlık, mühendislik, finans ve hukuk firmaları için ideal yüksek prestijli düzen.',
    icon: Building,
    primaryColor: '#0F172A',
    secondaryColor: '#334155',
    accentColor: '#2563EB',
    fontFamily: 'Inter, sans-serif',
    headingFont: 'Plus Jakarta Sans, sans-serif',
    headerVariation: 'minimal',
    heroVariation: 'full_width',
    servicesVariation: 'grid',
    aboutVariation: 'image_right',
    ctaVariation: 'full_width',
    footerVariation: 'corporate',
  },
  {
    id: 'STYLE_MODERN',
    name: 'STYLE 02 — Modern',
    badge: 'GENİŞ & DİNAMİK',
    tagline: 'Büyük typography, ferah beyaz boşluklar ve modern grid.',
    description: 'Teknoloji, SaaS, mimarlık, ajans ve yenilikçi servis şirketleri için çarpıcı görünüm.',
    icon: Zap,
    primaryColor: '#18181B',
    secondaryColor: '#71717A',
    accentColor: '#10B981',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    headingFont: 'Syne, sans-serif',
    headerVariation: 'split',
    heroVariation: 'split',
    servicesVariation: 'cards',
    aboutVariation: 'image_left',
    ctaVariation: 'dark',
    footerVariation: 'large',
  },
  {
    id: 'STYLE_PREMIUM',
    name: 'STYLE 03 — Premium',
    badge: 'LÜKS & DERİNLİK',
    tagline: 'Güçlü görseller, büyük hero alanları ve premium altın detaylar.',
    description: 'Lüks gayrimenkul, yat kiralama, özel klinik ve VIP danışmanlık için lüks atmosfer.',
    icon: Crown,
    primaryColor: '#09090B',
    secondaryColor: '#27272A',
    accentColor: '#D97706',
    fontFamily: 'Outfit, sans-serif',
    headingFont: 'Playfair Display, serif',
    headerVariation: 'transparent',
    heroVariation: 'full_width',
    servicesVariation: 'numbered',
    aboutVariation: 'image_right',
    ctaVariation: 'image_bg',
    footerVariation: 'large',
  },
  {
    id: 'STYLE_LOCAL_BUSINESS',
    name: 'STYLE 04 — Local Business',
    badge: 'DÖNÜŞÜM ODAKLI',
    tagline: 'Yerel işletmeler için doğrudan arama, WhatsApp ve lokasyon odaklı.',
    description: 'Halı yıkama, oto kiralama, temizlik, teknik servis ve yerel esnaflar için en yüksek dönüşüm getiren yapı.',
    icon: MapPin,
    primaryColor: '#1E293B',
    secondaryColor: '#475569',
    accentColor: '#0284C7',
    fontFamily: 'Inter, sans-serif',
    headingFont: 'Plus Jakarta Sans, sans-serif',
    headerVariation: 'split',
    heroVariation: 'image_right',
    servicesVariation: 'grid',
    aboutVariation: 'image_left',
    ctaVariation: 'full_width',
    footerVariation: 'corporate',
  },
];

export const DesignSystemManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData } = useSite();

  const s = activeSite?.settings || {};

  const [selectedStyle, setSelectedStyle] = useState<CorporateDesignStyle>(
    s.designStyle || 'STYLE_CORPORATE'
  );
  const [primaryColor, setPrimaryColor] = useState(s.primaryColor || '#0F172A');
  const [secondaryColor, setSecondaryColor] = useState(s.secondaryColor || '#334155');
  const [accentColor, setAccentColor] = useState(s.accentColor || '#2563EB');
  const [fontFamily, setFontFamily] = useState(s.fontFamily || 'Inter, sans-serif');
  const [headingFont, setHeadingFont] = useState(s.headingFont || 'Plus Jakarta Sans, sans-serif');

  // Variations
  const [headerVariation, setHeaderVariation] = useState<HeaderVariation>(s.headerVariation || 'minimal');
  const [heroVariation, setHeroVariation] = useState<HeroVariation>(s.heroVariation || 'full_width');
  const [servicesVariation, setServicesVariation] = useState<ServicesVariation>(s.servicesVariation || 'grid');
  const [aboutVariation, setAboutVariation] = useState<AboutVariation>(s.aboutVariation || 'image_right');
  const [ctaVariation, setCtaVariation] = useState<CtaVariation>(s.ctaVariation || 'full_width');
  const [footerVariation, setFooterVariation] = useState<FooterVariation>(s.footerVariation || 'corporate');

  const [saveToast, setSaveToast] = useState(false);

  useEffect(() => {
    if (activeSite?.settings) {
      const cfg = activeSite.settings;
      if (cfg.designStyle) setSelectedStyle(cfg.designStyle);
      if (cfg.primaryColor) setPrimaryColor(cfg.primaryColor);
      if (cfg.secondaryColor) setSecondaryColor(cfg.secondaryColor);
      if (cfg.accentColor) setAccentColor(cfg.accentColor);
      if (cfg.fontFamily) setFontFamily(cfg.fontFamily);
      if (cfg.headingFont) setHeadingFont(cfg.headingFont);
      if (cfg.headerVariation) setHeaderVariation(cfg.headerVariation);
      if (cfg.heroVariation) setHeroVariation(cfg.heroVariation);
      if (cfg.servicesVariation) setServicesVariation(cfg.servicesVariation);
      if (cfg.aboutVariation) setAboutVariation(cfg.aboutVariation);
      if (cfg.ctaVariation) setCtaVariation(cfg.ctaVariation);
      if (cfg.footerVariation) setFooterVariation(cfg.footerVariation);
    }
  }, [activeSite?.id, activeSite?.settings]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Tasarım Sistemi İçin Aktif Web Sitesi Gereklidir"
        description="Renk paletleri, tipografi ayarları ve hazır tasarım stillerini özelleştirmek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini seçin."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleApplyPreset = (preset: DesignStylePreset) => {
    setSelectedStyle(preset.id);
    setPrimaryColor(preset.primaryColor);
    setSecondaryColor(preset.secondaryColor);
    setAccentColor(preset.accentColor);
    setFontFamily(preset.fontFamily);
    setHeadingFont(preset.headingFont);
    setHeaderVariation(preset.headerVariation);
    setHeroVariation(preset.heroVariation);
    setServicesVariation(preset.servicesVariation);
    setAboutVariation(preset.aboutVariation);
    setCtaVariation(preset.ctaVariation);
    setFooterVariation(preset.footerVariation);
  };

  const handleSave = () => {
    if (!activeSite) return;
    // Update site settings
    const updatedSettings = {
      ...(activeSite?.settings || {}),
      designStyle: selectedStyle,
      primaryColor,
      secondaryColor,
      accentColor,
      fontFamily,
      headingFont,
      headerVariation,
      heroVariation,
      servicesVariation,
      aboutVariation,
      ctaVariation,
      footerVariation,
    };

    // Also update all section layout/variation props across pages
    const updatedPages: SitePage[] = (activeSite.pages || []).map((p) => {
      const updatedSections: PageSection[] = (p?.sections || []).map((sec) => {
        if (sec.type === 'HEADER') {
          return { ...sec, content: { ...sec.content, variation: headerVariation } };
        }
        if (sec.type === 'HERO') {
          return { ...sec, content: { ...sec.content, variation: heroVariation } };
        }
        if (sec.type === 'SERVICES') {
          return { ...sec, content: { ...sec.content, variation: servicesVariation } };
        }
        if (sec.type === 'ABOUT') {
          return { ...sec, content: { ...sec.content, variation: aboutVariation } };
        }
        if (sec.type === 'RESERVATION_CTA' || sec.type === 'CONTACT') {
          return { ...sec, content: { ...sec.content, variation: ctaVariation } };
        }
        if (sec.type === 'FOOTER') {
          return { ...sec, content: { ...sec.content, variation: footerVariation } };
        }
        return sec;
      });
      return { ...p, sections: updatedSections };
    });

    updateSiteData({
      pages: updatedPages,
      settings: updatedSettings,
    });

    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 3000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-8 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-indigo-600">
            <Paintbrush className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Tasarım Sistemi & Varyasyonlar</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Site Tasarım Sistemi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl">
            İçerikleriniz (firma bilgileri, hizmetler, yorumlar) korunurken sitenizin tasarım stilini ve bileşen varyasyonlarını anında değiştirin.
          </p>
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-lg shadow-blue-500/20 transition flex items-center gap-2 self-start sm:self-auto"
        >
          {saveToast ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Save className="w-4 h-4" />}
          <span>{saveToast ? 'Uygulandı ✓' : 'Tasarımı Uygula'}</span>
        </button>
      </div>

      {saveToast && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Tasarım sistemi ve bileşen varyasyonları başarıyla güncellendi.</span>
        </div>
      )}

      {/* 1. 4 Core Design Systems */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
            <Layout className="w-4 h-4 text-blue-600" />
            <span>1. Ana Tasarım Sistemini Seçin</span>
          </h2>
          <span className="text-xs text-slate-400 font-medium">4 Farklı Mimari Stil</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {DESIGN_STYLES.map((style) => {
            const isSelected = selectedStyle === style.id;
            const Icon = style.icon;
            return (
              <div
                key={style.id}
                onClick={() => handleApplyPreset(style)}
                className={`p-6 rounded-2xl cursor-pointer border-2 transition-all relative overflow-hidden flex flex-col justify-between space-y-4 ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50/40 shadow-md ring-2 ring-blue-500/20'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-xs'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                          isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-sm text-slate-900">{style.name}</h3>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                          {style.badge}
                        </span>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-xs">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-slate-600 font-medium pt-1">{style.tagline}</p>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{style.description}</p>
                </div>

                {/* Color preview dots */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100/80 text-[11px] text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ backgroundColor: style.primaryColor }} />
                    <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ backgroundColor: style.secondaryColor }} />
                    <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ backgroundColor: style.accentColor }} />
                  </div>
                  <span className="font-mono text-[10px] text-slate-400">{style.headingFont.split(',')[0]}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. Component Variations Matrix */}
      <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-6">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <h2 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-600" />
            <span>2. Bileşen Düzeni Varyasyonları (Component Variations)</span>
          </h2>
          <span className="text-xs text-slate-400">Tek tıkla düzen değiştirme</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Header */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700">Header Varyasyonu</label>
            <select
              value={headerVariation}
              onChange={(e) => setHeaderVariation(e.target.value as HeaderVariation)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
            >
              <option value="minimal">Minimal (Logo Sol, Menü Sağ)</option>
              <option value="centered">Centered (Ortalanmış Menü)</option>
              <option value="split">Split (İletişim & CTA Vurgulu)</option>
              <option value="transparent">Transparent (Hero Üzeri Şeffaf)</option>
            </select>
          </div>

          {/* Hero */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700">Hero Varyasyonu</label>
            <select
              value={heroVariation}
              onChange={(e) => setHeroVariation(e.target.value as HeroVariation)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
            >
              <option value="full_width">Full Width (Arka Plan Görseli + Metin)</option>
              <option value="image_right">Image Right (Sol Metin, Sağ Görsel)</option>
              <option value="image_left">Image Left (Sol Görsel, Sağ Metin)</option>
              <option value="split">Split (50/50 Dinamik Bölünmüş)</option>
              <option value="minimal">Minimal (Tipografik Sade)</option>
            </select>
          </div>

          {/* Services */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700">Hizmetler Varyasyonu</label>
            <select
              value={servicesVariation}
              onChange={(e) => setServicesVariation(e.target.value as ServicesVariation)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
            >
              <option value="grid">Grid (4'lü Modern Izgara)</option>
              <option value="cards">Cards (Görselli & Fiyatlı Kartlar)</option>
              <option value="list">List (Yatay Liste & Açıklama)</option>
              <option value="numbered">Numbered (Numaralandırılmış Süreç)</option>
            </select>
          </div>

          {/* About */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700">Hakkımızda Varyasyonu</label>
            <select
              value={aboutVariation}
              onChange={(e) => setAboutVariation(e.target.value as AboutVariation)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
            >
              <option value="image_right">Image Right (Sağ Görsel, Sol Metin)</option>
              <option value="image_left">Image Left (Sol Görsel, Sağ Metin)</option>
              <option value="full_width">Full Width (Geniş Kurumsal Blok)</option>
            </select>
          </div>

          {/* CTA */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700">CTA (Harekete Geçirici) Varyasyonu</label>
            <select
              value={ctaVariation}
              onChange={(e) => setCtaVariation(e.target.value as CtaVariation)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
            >
              <option value="full_width">Full Width (Renkli Arka Planlı)</option>
              <option value="dark">Dark (Koyu Zemin & Yüksek Kontrast)</option>
              <option value="image_bg">Image Background (Fotoğraf Üzeri)</option>
              <option value="minimal">Minimal (Sade Çizgisel)</option>
            </select>
          </div>

          {/* Footer */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700">Footer Varyasyonu</label>
            <select
              value={footerVariation}
              onChange={(e) => setFooterVariation(e.target.value as FooterVariation)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
            >
              <option value="corporate">Corporate (4 Kolonlu Kurumsal)</option>
              <option value="large">Large (Harita, Çalışma Saatleri & Menü)</option>
              <option value="compact">Compact (Sade Kompakt)</option>
            </select>
          </div>
        </div>
      </div>

      {/* 3. Renkler & Typography */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Colors */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
          <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
            <Palette className="w-4 h-4 text-pink-600" />
            <span>Renk Paleti</span>
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700">Ana Renk (Primary)</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-8 h-8 rounded-lg cursor-pointer border border-slate-300"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-24 px-2 py-1 text-xs font-mono rounded bg-slate-50 border border-slate-300"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700">İkincil Renk (Secondary)</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-8 h-8 rounded-lg cursor-pointer border border-slate-300"
                />
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-24 px-2 py-1 text-xs font-mono rounded bg-slate-50 border border-slate-300"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700">Vurgu Rengi (Accent)</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-8 h-8 rounded-lg cursor-pointer border border-slate-300"
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-24 px-2 py-1 text-xs font-mono rounded bg-slate-50 border border-slate-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Typography */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
          <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
            <Type className="w-4 h-4 text-indigo-600" />
            <span>Tipografi Eşleşmesi</span>
          </h3>

          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Başlık Yazı Tipi (Heading Font)</label>
              <select
                value={headingFont}
                onChange={(e) => setHeadingFont(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-medium text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
              >
                <option value="Plus Jakarta Sans, sans-serif">Plus Jakarta Sans (Kurumsal & Modern)</option>
                <option value="Syne, sans-serif">Syne (Cesur & Geometrik)</option>
                <option value="Playfair Display, serif">Playfair Display (Lüks & Zarif)</option>
                <option value="Outfit, sans-serif">Outfit (Temiz & Premium)</option>
                <option value="Montserrat, sans-serif">Montserrat (Klasik Güçlü)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Gövde Metni (Body Font)</label>
              <select
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-medium text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden"
              >
                <option value="Inter, sans-serif">Inter (Maksimum Okunabilirlik)</option>
                <option value="Plus Jakarta Sans, sans-serif">Plus Jakarta Sans</option>
                <option value="DM Sans, sans-serif">DM Sans</option>
                <option value="Open Sans, sans-serif">Open Sans</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
